import { Mail, MailOpen } from '@tamagui/lucide-icons';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { Row } from '@unistocks/components/layout/flex';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { EmptyList } from '@unistocks/components/no-data/empty-list';
import { Page } from '@unistocks/components/page/page';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { H4, Paragraph } from '@unistocks/components/text/text';
import { ListRenderItem } from '@unistocks/components/virtual-list/list';
import { invalidateNotificationsGetListQuery } from '@unistocks/features/notifications/invalidate-notification-list';
import { RelatedEntities } from '@unistocks/features/notifications/related-entities/related-entities';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@unistocks/features/notifications/utils';
import {
  NotificationDto,
  PagedResultDtoOfNotificationDto,
  ReadStatus
} from '@unistocks/proxy/axios-client';
import { notificationsGetList } from '@unistocks/proxy/axios-client/NotificationsClient';
import {
  notificationsGetUnreadNotificationsCountQueryKey,
  useNotificationsDeleteMutationWithParameters,
  useNotificationsMarkAsReadMutationWithParameters
} from '@unistocks/proxy/axios-client/NotificationsQuery';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { formatDateTime, t } from '@unistocks/shared/localization/index.ts';
import { extractId } from '@unistocks/shared/utils/list-utils';
import { clone } from '@unistocks/shared/utils/util';
import React, { useCallback, useState } from 'react';
import { GetProps, ListItem, Separator, useControllableState } from 'tamagui';

type ListHeader = { id: 'read' | 'unread'; title: string };
type NotificationQuery = InfiniteData<
  PagedResultDtoOfNotificationDto,
  {
    skipCount: number;
    maxResultCount: number;
  }
>;

const isListHeader = (item: any): item is ListHeader =>
  item && (item.id === 'read' || item.id === 'unread');

export const Notifications: React.FC = () => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<NotificationDto>();

  const [open, onOpenChange] = useControllableState({
    defaultProp: false,
    prop: !!selected,
    onChange: (setOpen: boolean) => {
      if (!setOpen) {
        setSelected(undefined);

        if (selected?.status === ReadStatus.Unread) {
          markAsRead({ id: selected.id });
        }
      }
    }
  });

  const { data, isSuccess, isRefetching, fetchNextPage, refetch } = useInfiniteQuery({
    initialPageParam: { skipCount: 0, maxResultCount: MAX_RESULT_COUNT },
    queryKey: NOTIFICATIONS_LIST_QUERY_KEY,
    queryFn: ({ pageParam: { skipCount, maxResultCount }, signal }) =>
      notificationsGetList(skipCount, maxResultCount, { signal }),
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const nextPage = lastPageParam.skipCount / MAX_RESULT_COUNT + 1;
      return nextPage * MAX_RESULT_COUNT < lastPage.totalCount
        ? { skipCount: nextPage * MAX_RESULT_COUNT, maxResultCount: MAX_RESULT_COUNT }
        : undefined;
    },
    select: (data: NotificationQuery) => {
      const items = data.pages.flatMap(page => page.items);

      if (!items.length) return items;

      const read = items.filter(item => item.status === ReadStatus.Read);
      const unread = items.filter(item => item.status === ReadStatus.Unread);

      return [
        unread.length
          ? {
              id: 'unread',
              title: t('::Notification:Unread')
            }
          : null,
        ...unread,
        read.length && unread.length
          ? {
              id: 'read',
              title: t('::Notification:Read')
            }
          : null,
        ...read
      ].filter(Boolean) as (NotificationDto & ListHeader)[];
    }
  });
  // Force rerender when notification status changes
  const readStatus = data?.reduce((acc, item) => acc + (item.status ?? ''), '');

  const { mutate: markAsRead } = useNotificationsMarkAsReadMutationWithParameters({
    onMutate: ({ id }) => {
      queryClient.setQueryData<NotificationQuery>(NOTIFICATIONS_LIST_QUERY_KEY, old => {
        if (!old) return old;

        for (const page of old.pages) {
          for (const notification of page.items) {
            if (notification.id === id) {
              notification.status = ReadStatus.Read;
              return clone(old);
            }
          }
        }

        return old;
      });
    },
    onError: () => {
      invalidateNotificationsGetListQuery(queryClient);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notificationsGetUnreadNotificationsCountQueryKey()
      });
    }
  });

  const { mutate: deleteNotification, isPending: isDeletePending } =
    useNotificationsDeleteMutationWithParameters({
      onMutate: ({ id }) => {
        onOpenChange(false);

        queryClient.setQueryData<NotificationQuery>(NOTIFICATIONS_LIST_QUERY_KEY, old => {
          if (!old) return old;

          for (const page of old.pages) {
            for (let i = 0; i < page.items.length; i++) {
              if (page.items[i].id === id) {
                page.items.splice(i, 1);
                return clone(old);
              }
            }
          }

          return old;
        });
      },
      onError: () => {
        invalidateNotificationsGetListQuery(queryClient);
      }
    });

  const renderNotificationListItem: ListRenderItem<NotificationDto> = useCallback(
    ({ item: notification }) => {
      if (isListHeader(notification)) {
        const Icon = notification.id === 'unread' ? Mail : MailOpen;
        return (
          <Row gap={'$3'} ml={'$3'} my={'$3'} alignItems="center">
            <Icon size={'$3'} color={'$unistocks'} />
            <H4>{notification.title}</H4>
          </Row>
        );
      }

      const Icon = notification.status === ReadStatus.Unread ? Mail : MailOpen;
      const iconColor: GetProps<typeof Icon>['color'] =
        notification.status === ReadStatus.Unread ? '$unistocks' : '$gray10';

      return (
        <ListItem
          cursor="pointer"
          gap={'$3'}
          title={notification.title}
          subTitle={formatDateTime(notification.creationTime)}
          scaleIcon={1.5}
          iconAfter={<Icon color={iconColor} />}
          onPress={() => setSelected(notification)}
        />
      );
    },
    []
  );

  return (
    <Page
      size="small"
      queryState={{
        queryKeys: [NOTIFICATIONS_LIST_QUERY_KEY],
        loadingText: t('::Norifications:Loading')
      }}
    >
      <Page.VirtualList
        data={data}
        extraData={readStatus}
        renderItem={renderNotificationListItem}
        keyExtractor={extractId}
        refreshing={isRefetching}
        onRefresh={refetch}
        onEndReached={fetchNextPage}
        onEndReachedThreshold={0.75}
        ItemSeparatorComponent={Separator}
        ListEmptyComponent={() =>
          isSuccess ? (
            <EmptyList noDataIcon={MailOpen} noDataText={t('::Notifications:NoNotifications')} />
          ) : null
        }
        headerTitle={t('::Menu:Notifications')}
        headerNotificationIcon={false}
      />

      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        headerIcon={<HeaderIcon Icon={selected?.status === ReadStatus.Unread ? Mail : MailOpen} />}
        headerTitle={selected?.title}
        headerSubtitle={selected?.creationTime ? formatDateTime(selected.creationTime) : undefined}
        headerRight={
          isDeletePending ? (
            <Spinner />
          ) : (
            <ConfirmDelete
              title={t('::Notification:Delete:Title')}
              message={t('::Notification:Delete:Message')}
              onConfirm={() => deleteNotification({ id: selected!.id })}
            />
          )
        }
        contentTopMargin
      >
        {selected?.message ? <Paragraph>{selected.message}</Paragraph> : null}
        {selected ? (
          <RelatedEntities notification={selected} onComplete={() => onOpenChange(false)} />
        ) : null}
      </Sheet>
    </Page>
  );
};

Notifications.displayName = 'Notifications';
